import { Injectable, Signal, signal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { deepMerge } from '@utils/helpers';
import { REACTION } from '@constants/app.constants';
import {
  IWorldStreamResource,
  FileResponse,
  My_actions,
  StreamExtendedResource,
  StreamExtendedResourceLocation_source,
  UserTinyResource,
  VideoResource,
  WorldStreamResourceLocation_source,
} from '@api/client';
import { VplayStreamRuntimeMetrics } from '../interfaces/vplay-stream-runtime-metrics.interface';

export type VplayProfileSource = 'chat' | 'custom';

@Injectable({ providedIn: 'root' })
export class VplayProfileStreamsService {
  private readonly streamsSignal = signal<IWorldStreamResource[]>([]);
  private readonly isActiveSignal = signal<boolean>(false);
  private readonly backHrefSignal = signal<string | null>(null);
  private readonly sourceSignal = signal<VplayProfileSource | null>(null);
  private readonly initialTargetIdSignal = signal<number | null>(null);
  private readonly isLoadingSubject = new BehaviorSubject<boolean>(false);

  public setProfileStreams(
    streams: StreamExtendedResource[] | null | undefined,
    options?: { backHref?: string }
  ): void {
    const adapted = (streams ?? [])
      .map((stream) => this.adaptExtendedStream(stream))
      .filter((stream): stream is IWorldStreamResource => !!stream);
    this.setVideos(adapted);
    this.backHrefSignal.set(options?.backHref ?? null);
    this.sourceSignal.set(null);
    this.initialTargetIdSignal.set(null);
    this.isLoadingSubject.next(false);
  }

  public setCustomStreams(
    streams: IWorldStreamResource[] | null | undefined,
    options?: { backHref?: string; source?: VplayProfileSource; initialTargetId?: number | null }
  ): void {
    const normalized = this.normalizeStreams(streams ?? []);
    this.setVideos(normalized);
    this.backHrefSignal.set(options?.backHref ?? null);
    this.sourceSignal.set(options?.source ?? 'custom');
    this.initialTargetIdSignal.set(options?.initialTargetId ?? null);
    this.isLoadingSubject.next(false);
  }

  public clear(): void {
    this.setVideos([]);
    this.backHrefSignal.set(null);
    this.sourceSignal.set(null);
    this.initialTargetIdSignal.set(null);
    this.isLoadingSubject.next(false);
  }

  public isActive(): boolean {
    return this.isActiveSignal();
  }

  public getIsActiveSignal(): Signal<boolean> {
    return this.isActiveSignal.asReadonly();
  }

  public getSource(): VplayProfileSource | null {
    return this.sourceSignal();
  }

  public getInitialTargetId(): number | null {
    return this.initialTargetIdSignal();
  }

  public getBackHref(): string | null {
    return this.backHrefSignal();
  }

  public getVideoListSignal(): Signal<IWorldStreamResource[]> {
    return this.streamsSignal.asReadonly();
  }

  public isLoading(): Observable<boolean> {
    return this.isLoadingSubject.asObservable();
  }

  public ensureInitialLoad(): void {
    this.isLoadingSubject.next(false);
  }

  public resetList(): void {
    this.setVideos([]);
    this.backHrefSignal.set(null);
    this.sourceSignal.set(null);
    this.initialTargetIdSignal.set(null);
    this.isLoadingSubject.next(false);
  }

  public loadNext(): void {
    // no-op for profile list
  }

  public async loadSingleForSsr(video?: number): Promise<IWorldStreamResource | null> {
    const normalizedVideoId = this.normalizeVideoId(video ?? null);
    if (normalizedVideoId == null) {
      return null;
    }
    const streams = this.streamsSignal();
    return streams.find((stream) => stream.video_id === normalizedVideoId) ?? null;
  }

  public updateVideoLike(id: number, value: REACTION | null): void {
    const normalizedId = this.normalizeVideoId(id);
    if (normalizedId == null) {
      return;
    }

    const videos = this.streamsSignal();
    const idx = videos.findIndex(video => video.video_id === normalizedId);
    if (idx === -1) {
      return;
    }

    const stream = videos[idx];
    const currentReaction = stream.my_actions?.my_reaction ?? null;
    const nextReaction = value ?? null;

    if (currentReaction === nextReaction) {
      return;
    }

    let nextLikes = stream.actions?.likes ?? 0;

    if (nextReaction === REACTION.LIKE) {
      nextLikes += 1;
    } else if (currentReaction === REACTION.LIKE) {
      nextLikes = Math.max(0, nextLikes - 1);
    }

    const my_actions = new My_actions({ ...(stream.my_actions ?? {}), my_reaction: nextReaction });
    const actions = new VideoResource({ ...(stream.actions ?? {}), likes: nextLikes });

    const updatedStream = {
      ...stream,
      my_actions,
      actions
    } as IWorldStreamResource;

    const nextVideos = [...videos];
    nextVideos[idx] = updatedStream;
    this.setVideos(nextVideos);
  }

  public updateVideoComment(id: number, value: number): void {
    const stream = this.streamsSignal().find((x) => x.video_id === id);

    if (stream) {
      const current = stream.actions?.comments_count ?? 0;
      if (current === value) {
        return;
      }

      const res = {
        actions: { comments_count: value },
      } as IWorldStreamResource;

      this.updateVideoInList(id, res);
    }
  }

  public updateVideoWatches(id: number, value: number): void {
    const normalizedId = this.normalizeVideoId(id);
    if (normalizedId == null) {
      return;
    }

    const videos = this.streamsSignal();
    const idx = videos.findIndex(video => video.video_id === normalizedId);
    if (idx === -1) {
      return;
    }

    const stream = videos[idx];
    const watches = Math.max(0, Math.floor(value));

    if (Math.max(0, Math.floor(stream.actions?.watches ?? 0)) === watches) {
      return;
    }

    const actions = new VideoResource({ ...(stream.actions ?? {}), watches });

    const updatedStream = {
      ...stream,
      actions
    } as IWorldStreamResource;

    const nextVideos = [...videos];
    nextVideos[idx] = updatedStream;
    this.setVideos(nextVideos);
  }

  public updateStreamLiveViewers(id: number, value: number): void {
    const normalizedId = this.normalizeVideoId(id);
    if (normalizedId == null) {
      return;
    }

    const videos = this.streamsSignal();
    const idx = videos.findIndex(video => video.video_id === normalizedId);
    if (idx === -1) {
      return;
    }

    const stream = videos[idx];
    const liveViewers = Math.max(0, Math.floor(value));

    if (stream?.['live_viewers'] === liveViewers) {
      return;
    }

    const updatedStream = {
      ...stream,
      live_viewers: liveViewers,
    } as IWorldStreamResource;

    const nextVideos = [...videos];
    nextVideos[idx] = updatedStream;
    this.setVideos(nextVideos);
  }

  public updateStreamLiveState(id: number, live: boolean, forceEnded: boolean = false): void {
    const normalizedId = this.normalizeVideoId(id);
    if (normalizedId == null) {
      return;
    }

    const videos = this.streamsSignal();
    const idx = videos.findIndex((video) => video.video_id === normalizedId);
    if (idx === -1) {
      return;
    }

    const stream = videos[idx];
    const wasLive = !!stream.live;
    const wasEnded = !!stream?.['live_ended'];
    const liveEnded = live ? false : (forceEnded ? true : (wasEnded || wasLive));

    if (wasLive === live && wasEnded === liveEnded) {
      return;
    }

    const updatedStream: IWorldStreamResource = {
      ...stream,
      live,
      live_ended: live ? false : liveEnded,
      ...(live ? {} : { live_viewers: null }),
    };

    const nextVideos = [...videos];
    nextVideos[idx] = updatedStream;
    this.setVideos(nextVideos);
  }

  public markVideoAsWatched(id: number): void {
    const normalizedId = this.normalizeVideoId(id);
    if (normalizedId == null) {
      return;
    }

    const videos = this.streamsSignal();
    const idx = videos.findIndex(video => video.video_id === normalizedId);
    if (idx === -1) {
      return;
    }

    const stream = videos[idx];
    if (stream.my_actions?.i_watched) {
      return;
    }

    const my_actions = new My_actions({ ...(stream.my_actions ?? {}), i_watched: true });

    const updatedStream = {
      ...stream,
      my_actions,
    } as IWorldStreamResource;

    const nextVideos = [...videos];
    nextVideos[idx] = updatedStream;
    this.setVideos(nextVideos);
  }

  public updateUserFollowing(author_id: number, follow: boolean): void {
    const videos = this.streamsSignal();
    let changed = false;
    const nextVideos = videos.map(item => {
      if (item.author_id !== author_id) {
        return item;
      }

      const current = item.my_actions?.i_subscribed ?? false;
      if (current === follow) {
        return item;
      }

      changed = true;
      const my_actions = new My_actions({ ...(item.my_actions ?? {}), i_subscribed: follow });
      return {
        ...item,
        my_actions,
      } as IWorldStreamResource;
    });

    if (changed) {
      this.setVideos(nextVideos);
    }
  }

  public updateStreamInfo(id: number, title: string, descr: string): void {
    const videos = this.streamsSignal();
    let changed = false;
    const nextVideos = videos.map(item => {
      if (item.video_id !== id) {
        return item;
      }

      if (item.name === title && item.description === descr) {
        return item;
      }

      changed = true;
      return {
        ...item,
        name: title,
        description: descr,
      } as IWorldStreamResource;
    });

    if (changed) {
      this.setVideos(nextVideos);
    }
  }

  public updateStreamActions(id: number, actions: VideoResource | null, myActions: My_actions | null): void {
    const normalizedId = this.normalizeVideoId(id);
    if (normalizedId == null) {
      return;
    }
    if (!actions && !myActions) {
      return;
    }

    const videos = this.streamsSignal();
    const idx = videos.findIndex((video) => video.video_id === normalizedId);
    if (idx === -1) {
      return;
    }

    const stream = videos[idx];
    const nextActions = actions ?? stream.actions;
    const nextMyActions = myActions ?? stream.my_actions;

    if (nextActions === stream.actions && nextMyActions === stream.my_actions) {
      return;
    }

    const nextStream: IWorldStreamResource = {
      ...stream,
      actions: nextActions,
      my_actions: nextMyActions,
    };

    const nextVideos = [...videos];
    nextVideos[idx] = nextStream;
    this.setVideos(nextVideos);
  }

  public updateStreamRuntimeMetrics(id: number, patch: VplayStreamRuntimeMetrics): void {
    const normalizedId = this.normalizeVideoId(id);
    if (normalizedId == null) {
      return;
    }

    const videos = this.streamsSignal();
    const idx = videos.findIndex((video) => video.video_id === normalizedId);
    if (idx === -1) {
      return;
    }

    const stream = videos[idx];
    const nextActions = this.buildRuntimeActions(stream.actions, patch);
    const hasLive = typeof patch.live === 'boolean';
    const nextLive = hasLive ? patch.live : stream.live;
    const nextLiveEnded = this.resolveRuntimeLiveEnded(stream, patch);
    const nextLiveViewers = this.resolveRuntimeLiveViewers(stream, patch);
    const currentLiveEnded = this.normalizeBooleanFlag(stream['live_ended']);
    const currentLiveViewers = this.normalizeMetric(stream['live_viewers']);

    const liveChanged = nextLive !== stream.live;
    const liveEndedChanged = nextLiveEnded !== currentLiveEnded;
    const liveViewersChanged = nextLiveViewers !== currentLiveViewers;
    const actionsChanged = nextActions !== stream.actions;

    if (!liveChanged && !liveEndedChanged && !liveViewersChanged && !actionsChanged) {
      return;
    }

    const nextStream: IWorldStreamResource = {
      ...stream,
      live: nextLive,
      actions: nextActions,
    };

    if (liveViewersChanged) {
      nextStream['live_viewers'] = nextLiveViewers;
    }

    if (liveEndedChanged) {
      nextStream['live_ended'] = nextLiveEnded;
    }

    const nextVideos = [...videos];
    nextVideos[idx] = nextStream;
    this.setVideos(nextVideos);
  }

  public deleteStream(id: number): void {
    const videos = this.streamsSignal();
    const nextVideos = videos.filter(item => item.video_id !== id);
    if (nextVideos.length !== videos.length) {
      this.setVideos(nextVideos);
    }
  }

  private updateVideoInList(id: number, patch: Partial<IWorldStreamResource>): void {
    const videos = this.streamsSignal();
    const normalizedId = this.normalizeVideoId(id);
    if (normalizedId == null) {
      return;
    }

    const idx = videos.findIndex((v) => v.video_id === normalizedId);

    if (idx !== -1) {
      const updated = deepMerge({ ...videos[idx] }, patch);
      const newArr = [...videos];

      newArr[idx] = updated;
      this.setVideos(newArr);
    }
  }

  private setVideos(videos: IWorldStreamResource[]): void {
    const normalized = this.normalizeStreams(videos);
    this.streamsSignal.set(normalized);
    this.isActiveSignal.set(normalized.length > 0);
    if (normalized.length === 0) {
      this.sourceSignal.set(null);
      this.initialTargetIdSignal.set(null);
    }
  }

  private normalizeStreams(streams: IWorldStreamResource[]): IWorldStreamResource[] {
    if (!Array.isArray(streams)) {
      return [];
    }

    return streams.map(stream => this.normalizeStream(stream));
  }

  private normalizeStream(stream: IWorldStreamResource): IWorldStreamResource {
    if (!stream) {
      return stream;
    }

    const normalizedId = this.normalizeVideoId(stream.video_id);

    if (normalizedId !== stream.video_id) {
      return {
        ...stream,
        video_id: normalizedId
      };
    }

    return stream;
  }

  private normalizeVideoId(value: number | string | null | undefined): number | undefined {
    if (value === null || value === undefined) {
      return undefined;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : undefined;
    }

    if (typeof value === 'string') {
      const parsed = Number(value.trim());
      return Number.isFinite(parsed) ? parsed : undefined;
    }

    return undefined;
  }

  private buildRuntimeActions(
    actions: VideoResource | undefined,
    patch: VplayStreamRuntimeMetrics
  ): VideoResource | undefined {
    const nextLikes = this.normalizeMetric(patch.likes);
    const nextWatches = this.normalizeMetric(patch.watches);
    const nextCommentsCount = this.normalizeMetric(patch.commentsCount);

    if (nextLikes === null && nextWatches === null && nextCommentsCount === null) {
      return actions;
    }

    const base = actions ?? new VideoResource({});
    const likes = nextLikes ?? this.normalizeMetric(base.likes);
    const watches = nextWatches ?? this.normalizeMetric(base.watches);
    const commentsCount = nextCommentsCount ?? this.normalizeMetric(base.comments_count);

    if (
      likes === this.normalizeMetric(actions?.likes)
      && watches === this.normalizeMetric(actions?.watches)
      && commentsCount === this.normalizeMetric(actions?.comments_count)
    ) {
      return actions;
    }

    return new VideoResource({
      ...(base ?? {}),
      likes: likes ?? undefined,
      watches: watches ?? undefined,
      comments_count: commentsCount ?? undefined,
    });
  }

  private resolveRuntimeLiveEnded(
    stream: IWorldStreamResource,
    patch: VplayStreamRuntimeMetrics
  ): boolean | null {
    if (typeof patch.liveEnded === 'boolean') {
      return patch.liveEnded;
    }
    if (patch.live === true) {
      return false;
    }
    if (patch.live === false) {
      return this.normalizeBooleanFlag(stream['live_ended']) ?? !!stream.live;
    }
    return this.normalizeBooleanFlag(stream['live_ended']);
  }

  private resolveRuntimeLiveViewers(
    stream: IWorldStreamResource,
    patch: VplayStreamRuntimeMetrics
  ): number | null {
    const liveViewers = this.normalizeMetric(patch.liveViewers);
    if (liveViewers !== null) {
      return liveViewers;
    }
    if (patch.live === false) {
      return null;
    }
    return this.normalizeMetric(stream['live_viewers']);
  }

  private normalizeMetric(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return Math.max(0, Math.floor(value));
    }
    if (typeof value === 'string') {
      const parsed = Number(value.trim());
      if (Number.isFinite(parsed)) {
        return Math.max(0, Math.floor(parsed));
      }
    }
    return null;
  }

  private normalizeBooleanFlag(value: unknown): boolean | null {
    if (typeof value === 'boolean') {
      return value;
    }
    return null;
  }

  private adaptExtendedStream(
    stream: StreamExtendedResource | null | undefined,
  ): IWorldStreamResource | null {
    if (!stream) {
      return null;
    }

    const videoId = stream.video_id ?? stream.video?.id;
    const sourceUser = this.extractUserData(stream.user) ?? this.extractUserData(this.extractVideoUser(stream));
    const authorIdRaw = stream['author_id'];
    const authorId = sourceUser?.id ?? this.normalizeVideoId(authorIdRaw) ?? null;

    const actions = new VideoResource({
      id: videoId,
      likes: stream.video?.likes,
      dislikes: stream.video?.dislikes,
      watches: stream.video?.watches,
      comments_count: stream.video?.comments_count ?? stream.comments_count,
    });

    const myActions = new My_actions({
      my_reaction: stream.my_reaction,
      i_watched: stream.i_watched,
      i_subscribed: stream.i_subscribed,
    });

    const avatar = this.normalizeAvatar(sourceUser?.avatar);
    const photoUrl = this.normalizeAvatarUrl(sourceUser?.photo);
    const avatarUrl = this.normalizeAvatarUrl(sourceUser?.avatar_url);
    const user = sourceUser
      ? new UserTinyResource({
          id: sourceUser.id,
          name: sourceUser.name,
          surname: sourceUser.surname,
          official: sourceUser.official,
          avatar: avatar ?? undefined,
        })
      : undefined;
    if (user) {
      if (photoUrl) {
        user['photo'] = photoUrl;
      }
      if (avatarUrl) {
        user['avatar_url'] = avatarUrl;
      }
    }

    const extra = this.extractExtraFields(stream);
    const locationSource = this.normalizeLocationSource(stream.location_source);
    const liveViewers = this.normalizeMetric(stream['live_viewers']);
    const liveEnded = this.normalizeBooleanFlag(stream['live_ended']);

    const worldStream: IWorldStreamResource = {
      ...extra,
      video_id: videoId,
      author_id: authorId ?? undefined,
      live: stream.live,
      ...(liveViewers !== null ? { live_viewers: liveViewers } : {}),
      ...(liveEnded !== null ? { live_ended: liveEnded } : {}),
      actions,
      name: stream.name ?? stream.video?.name,
      description: stream.description ?? stream.place ?? undefined,
      location: stream.place ?? undefined,
      place: stream.place ?? undefined,
      location_source: locationSource,
      my_actions: myActions,
      user,
    };

    return this.normalizeStream(worldStream);
  }

  private extractVideoUser(stream: StreamExtendedResource): unknown {
    const videoValue = stream['video'];
    if (!this.isRecord(videoValue)) {
      return undefined;
    }
    return videoValue['user'];
  }

  private extractUserData(value: unknown): UserLike | null {
    if (!this.isRecord(value)) {
      return null;
    }
    const id = typeof value['id'] === 'number' ? value['id'] : undefined;
    const name = typeof value['name'] === 'string' ? value['name'] : undefined;
    const surname = typeof value['surname'] === 'string' ? value['surname'] : undefined;
    const official = typeof value['official'] === 'boolean' ? value['official'] : undefined;
    const avatar = value['avatar'];
    const photo = value['photo'];
    const avatarUrl = value['avatar_url'];

    return {
      id,
      name,
      surname,
      official,
      avatar,
      photo,
      avatar_url: avatarUrl,
    };
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return value != null && typeof value === 'object' && !Array.isArray(value);
  }

  private normalizeAvatar(value: unknown): FileResponse | undefined {
    if (!this.isRecord(value)) {
      return undefined;
    }
    return FileResponse.fromJS(value);
  }

  private normalizeAvatarUrl(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  private normalizeLocationSource(
    value: StreamExtendedResourceLocation_source | undefined
  ): WorldStreamResourceLocation_source | undefined {
    if (value === StreamExtendedResourceLocation_source.Gps) {
      return WorldStreamResourceLocation_source.Gps;
    }
    if (value === StreamExtendedResourceLocation_source.Ip) {
      return WorldStreamResourceLocation_source.Ip;
    }
    return undefined;
  }

  private extractExtraFields(stream: StreamExtendedResource): Record<string, unknown> {
    const extra: Record<string, unknown> = {};
    const fileValue = stream['file'];
    if (fileValue !== undefined) {
      extra['file'] = fileValue;
    }
    const fileUrl = stream['file_url'];
    if (typeof fileUrl === 'string' && fileUrl.trim().length > 0) {
      extra['file_url'] = fileUrl.trim();
    }
    const fileId = stream['file_id'];
    if (typeof fileId === 'number' && Number.isFinite(fileId)) {
      extra['file_id'] = fileId;
    } else if (typeof fileId === 'string' && fileId.trim().length > 0) {
      extra['file_id'] = fileId.trim();
    }
    const fileUserId = stream['file_user_id'];
    if (typeof fileUserId === 'number' && Number.isFinite(fileUserId)) {
      extra['file_user_id'] = fileUserId;
    } else if (typeof fileUserId === 'string' && fileUserId.trim().length > 0) {
      extra['file_user_id'] = fileUserId.trim();
    }
    const videoValue = stream['video'];
    if (videoValue !== undefined) {
      extra['video'] = videoValue;
    }
    if (stream.location) {
      extra['location_coordinates'] = stream.location;
    }
    if (typeof stream.place === 'string' && stream.place.trim().length > 0) {
      extra['place'] = stream.place.trim();
    }
    return extra;
  }
}

type UserLike = {
  id?: number;
  name?: string;
  surname?: string;
  official?: boolean;
  avatar?: unknown;
  photo?: unknown;
  avatar_url?: unknown;
};
